import React, { useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Button, ListGroup, Badge } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { showToast } from '../../../utils/toast';

const BillPaymentPayment = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const service = state?.service;
  const operator = state?.operator;
  const billDetails = state?.billDetails;
  const region = state?.region;
  const receiptRef = useRef(null);

  useEffect(() => {
    if (!service || !operator || !billDetails) {
      navigate('/bill-payments', { replace: true });
    }
  }, [service, operator, billDetails, navigate]);

  const payableAmount = useMemo(() => {
    const amount = Number(billDetails?.billAmount || 0);
    return Number.isNaN(amount) ? 0 : amount;
  }, [billDetails]);

  if (!service || !operator || !billDetails) {
    return null;
  }

  const handlePrintReceipt = () => {
    if (!receiptRef.current) {
      return;
    }

    const receiptContent = receiptRef.current.innerHTML;
    const printWindow = window.open('', 'PRINT', 'height=650,width=900');

    if (printWindow) {
      printWindow.document.write(`<!DOCTYPE html>
        <html>
          <head>
            <title>Bill Payment Receipt</title>
            <style>
              :root {
                color-scheme: light;
              }

              @page {
                margin: 18mm;
              }

              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                margin: 0;
                padding: 24px;
                background: #f5f7ff;
                color: #1e295d;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }

              .receipt-card {
                max-width: 720px;
                margin: 0 auto;
              }

              .receipt-wrapper {
                position: relative;
                background: #ffffff;
                border-radius: 28px;
                border: 1px solid rgba(80, 102, 255, 0.18);
                padding: 36px;
                box-shadow: 0 22px 50px rgba(30, 41, 93, 0.18);
                overflow: hidden;
              }

              .receipt-wrapper::before,
              .receipt-wrapper::after {
                content: '';
                position: absolute;
                left: -30px;
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background: rgba(52, 72, 203, 0.08);
              }

              .receipt-wrapper::before {
                top: 120px;
              }

              .receipt-wrapper::after {
                bottom: 120px;
              }

              .receipt-watermark {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 280px;
                max-width: 70%;
                opacity: 0.09;
                filter: drop-shadow(0 6px 18px rgba(11, 46, 104, 0.08));
              }

              .receipt-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 24px;
                padding-bottom: 18px;
                border-bottom: 1px dashed rgba(30, 41, 93, 0.16);
              }

              .receipt-brand {
                display: flex;
                align-items: center;
                gap: 18px;
              }

              .receipt-logo {
                width: 78px;
                height: 78px;
                border-radius: 20px;
                background: rgba(47, 76, 254, 0.1);
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 12px;
                box-shadow: inset 0 1px 4px rgba(255, 255, 255, 0.6);
              }

              .receipt-logo img {
                width: 100%;
                height: 100%;
                object-fit: contain;
              }

              .receipt-title {
                font-size: 20px;
                font-weight: 700;
                color: #1d285a;
                margin: 0 0 4px;
              }

              .receipt-meta {
                font-size: 13px;
                color: #7681a4;
                margin: 0;
              }

              .receipt-summary {
                display: flex;
                align-items: center;
                gap: 16px;
              }

              .amount-chip {
                display: inline-flex;
                align-items: center;
                font-weight: 700;
                font-size: 22px;
                color: #1d285a;
                background: rgba(47, 76, 254, 0.1);
                padding: 12px 28px;
                border-radius: 999px;
                box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
                border: 1px solid rgba(47, 76, 254, 0.25);
              }

              .receipt-body {
                margin-top: 30px;
                display: flex;
                flex-direction: column;
                gap: 22px;
                position: relative;
                z-index: 1;
              }

              .section-heading {
                margin: 0 0 10px;
                font-size: 12px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.12em;
                color: #5d6788;
              }

              .detail-list {
                display: flex;
                flex-direction: column;
                gap: 14px;
                padding-top: 12px;
              }

              .detail-row {
                display: flex;
                justify-content: space-between;
                gap: 14px;
                padding-bottom: 12px;
                border-bottom: 1px dashed rgba(93, 103, 136, 0.28);
              }

              .detail-row:last-child {
                border-bottom: none;
                padding-bottom: 0;
              }

              .detail-label {
                font-size: 13px;
                font-weight: 600;
                letter-spacing: 0.08em;
                color: #7a84a5;
              }

              .detail-value {
                font-size: 15px;
                font-weight: 600;
                color: #1e295d;
                text-align: right;
                flex: 1;
              }

              .receipt-section {
                border-bottom: 1px dashed rgba(174, 184, 255, 0.4);
                padding-bottom: 24px;
              }

              .receipt-section:last-of-type {
                border-bottom: none;
                padding-bottom: 0;
              }

              .receipt-total {
                margin-top: 20px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                border: 1px solid rgba(47, 76, 254, 0.22);
                border-radius: 16px;
                padding: 16px 20px;
                background: linear-gradient(135deg, rgba(47, 76, 254, 0.08), rgba(47, 76, 254, 0.02));
                box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
                font-weight: 700;
                color: #1d285a;
              }

              .total-amount {
                font-size: 20px;
              }

              .receipt-footer {
                margin-top: 32px;
                display: flex;
                flex-direction: column;
                gap: 8px;
                text-align: center;
                position: relative;
                z-index: 1;
              }

              .footer-note {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                font-weight: 600;
                font-size: 13px;
                color: #198754;
                background: rgba(25, 135, 84, 0.12);
                padding: 12px 18px;
                border-radius: 12px;
              }

              small {
                color: #7f88a9;
              }
            </style>
          </head>
          <body>
            <div class="receipt-card">
              ${receiptContent}
            </div>
          </body>
        </html>`);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  return (
    <Container fluid className="bill-payment-payment p-4">
      <Button
        className="back-button mb-3"
        onClick={() => navigate(-1)}
      >
        <i className="bi bi-arrow-left me-2"></i>
        Back
      </Button>

      <div className="page-header mb-4">
        <h4 className="mb-1" style={{ fontWeight: 600, color: '#2c2268' }}>
          Payment Overview
        </h4>
        <h6 className="text-muted mb-0">
          Verify the bill information below and proceed with a secure payment.
        </h6>
      </div>

      <Row className="g-4 justify-content-center">
        <Col lg={7} md={9}>
          <div className="receipt-wrapper" ref={receiptRef}>
            <img
              src="/b_assured.png"
              alt="B Assured"
              className="receipt-watermark"
            />
            <header className="receipt-header">
              <div className="receipt-brand">
                <div className="receipt-logo">
                  <img src="/BrandLogo.svg" alt="Shakti E Solution" />
                </div>
                <div>
                  <p className="receipt-title mb-1">Shakti E Solution Digital Receipt</p>
                  <p className="receipt-meta mb-0">Payment Overview • {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                </div>
              </div>
              <div className="receipt-summary">
                <div className="amount-chip">₹{payableAmount.toLocaleString('en-IN')}</div>
              </div>
            </header>

            <div className="receipt-body">
              <section className="receipt-section">
                <h6 className="section-heading">Service Details</h6>
                <div className="detail-list">
                  <div className="detail-row">
                    <span className="detail-label">Service</span>
                    <span className="detail-value">{service.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Operator</span>
                    <span className="detail-value">{operator.label}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Region</span>
                    <span className="detail-value">{region || 'Pan India'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">B Connect ID</span>
                    <span className="detail-value">{billDetails?.consumerId?.slice(-6) || 'NA'}</span>
                  </div>
                </div>
              </section>

              <section className="receipt-section">
                <h6 className="section-heading">Customer Details</h6>
                <div className="detail-list">
                  <div className="detail-row">
                    <span className="detail-label">Consumer Account</span>
                    <span className="detail-value">{billDetails.consumerId}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Customer Name</span>
                    <span className="detail-value">{billDetails.customerName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Mobile Number</span>
                    <span className="detail-value">{billDetails.mobileNumber}</span>
                  </div>
                </div>
              </section>

              <section className="receipt-section">
                <h6 className="section-heading">Bill Summary</h6>
                <div className="detail-list">
                  <div className="detail-row">
                    <span className="detail-label">Bill Amount</span>
                    <span className="detail-value">₹{payableAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Due Date</span>
                    <span className="detail-value">
                      {billDetails.dueDate
                        ? new Date(billDetails.dueDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })
                        : 'Not Available'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Convenience Fee</span>
                    <span className="detail-value">₹0.00</span>
                  </div>
                </div>
                <div className="receipt-total">
                  <span>Total Payable</span>
                  <span className="total-amount">₹{payableAmount.toLocaleString('en-IN')}</span>
                </div>
              </section>
            </div>

            <footer className="receipt-footer">
              <div className="footer-note">
                <i className="bi bi-shield-lock me-2"></i>
                Secured by BBPS • Instant confirmation receipt on success.
              </div>
              <small className="text-muted">Digital receipt will be shared with the customer via SMS / Email.</small>
            </footer>
          </div>

          <div className="text-center mt-3">
            <Button variant="light" onClick={handlePrintReceipt}>
              <i className="bi bi-printer me-2"></i>
              Print Receipt
            </Button>
          </div>
        </Col>
      </Row>

      <style jsx="true">{`
        .bill-payment-payment {
          background-color: #f8f9fc;
          min-height: calc(100vh - 100px);
        }

        .back-button {
          background-color: transparent;
          color: #1e1850;
          border: 1px solid rgba(30, 24, 80, 0.3);
          border-radius: 999px;
          font-weight: 600;
          padding: 8px 20px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
        }

        .back-button:hover,
        .back-button:focus {
          background-color: rgba(30, 24, 80, 0.1);
          color: #1e1850;
          box-shadow: 0 8px 16px rgba(44, 34, 104, 0.1);
        }

        .back-button:active {
          background-color: rgba(30, 24, 80, 0.18);
        }

        .receipt-wrapper {
          position: relative;
          background: #ffffff;
          border-radius: 24px;
          border: 1px solid #e4e9ff;
          box-shadow: 0 18px 40px rgba(32, 56, 101, 0.1);
          padding: 32px;
          overflow: hidden;
        }

        .receipt-watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 260px;
          max-width: 70%;
          opacity: 0.08;
          pointer-events: none;
          filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.05));
        }

        .receipt-wrapper::before,
        .receipt-wrapper::after {
          content: '';
          position: absolute;
          left: -28px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(52, 72, 203, 0.06);
          pointer-events: none;
        }

        .receipt-wrapper::before {
          top: 96px;
        }

        .receipt-wrapper::after {
          bottom: 96px;
        }

        .receipt-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          padding-bottom: 16px;
          border-bottom: 1px dashed rgba(30, 41, 93, 0.16);
        }

        .receipt-brand {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .receipt-logo {
          width: 95px;
          height: 50px;
          border-radius: 20px;
          background: rgba(47, 76, 254, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          box-shadow: inset 0 1px 4px rgba(255, 255, 255, 0.6);
        }

        .receipt-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .receipt-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e295d;
        }

        .receipt-meta {
          font-size: 13px;
          color: #7a84a5;
        }

        .receipt-summary {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        .amount-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-weight: 700;
          font-size: 22px;
          color: #1d285a;
          background: rgba(47, 76, 254, 0.1);
          padding: 12px 26px;
          border-radius: 999px;
          border: 1px solid rgba(47, 76, 254, 0.2);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
        }

        .receipt-body {
          margin-top: 28px;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .receipt-section {
          padding: 0;
        }

        .section-heading {
          margin-bottom: 8px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #58627f;
        }

        .detail-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 12px 0 0;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          padding-bottom: 12px;
          border-bottom: 1px dashed rgba(88, 98, 127, 0.25);
        }

        .detail-row:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }

        .detail-label {
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #7a84a5;
        }

        .detail-value {
          font-size: 15px;
          font-weight: 600;
          color: #1e295d;
          text-align: right;
          flex: 1;
          white-space: normal;
          word-break: break-word;
        }

        .receipt-section:not(:last-of-type) {
          border-bottom: 1px dashed rgba(174, 184, 255, 0.4);
          padding-bottom: 22px;
        }

        .receipt-total {
          margin-top: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #ffffff;
          border-radius: 14px;
          padding: 14px 18px;
          border: 1px solid rgba(47, 76, 254, 0.18);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
          font-weight: 700;
          color: #1d285a;
        }

        .total-amount {
          font-size: 18px;
        }

        .receipt-footer {
          margin-top: 28px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .footer-note {
          display: flex;
          align-items: center;
          font-weight: 600;
          font-size: 13px;
          color: #198754;
          background: rgba(25, 135, 84, 0.1);
          padding: 10px 14px;
          border-radius: 12px;
        }

        @media (max-width: 992px) {
          .receipt-wrapper {
            padding: 26px;
          }

          .receipt-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 18px;
          }

          .receipt-summary {
            align-self: stretch;
            justify-content: space-between;
          }

          .receipt-watermark {
            width: 220px;
          }

          .receipt-logo {
            width: 64px;
            height: 64px;
            padding: 10px;
          }

          .amount-chip {
            font-size: 20px;
            padding: 10px 22px;
          }
        }

        @media (max-width: 576px) {
          .detail-value {
            text-align: left;
          }

          .receipt-wrapper::before,
          .receipt-wrapper::after {
            display: none;
          }

          .receipt-watermark {
            width: 180px;
            opacity: 0.1;
          }

          .receipt-logo {
            width: 58px;
            height: 58px;
            padding: 9px;
          }
        }
      `}</style>
    </Container>
  );
};

export default BillPaymentPayment;
